import { useRegisterSW } from 'virtual:pwa-register/react';
import { UpdateToast } from './UpdateToast';

// How often to poll for a newly deployed service worker. iOS standalone PWAs
// update very lazily on their own, so we nudge them.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

export function PwaUpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      setInterval(() => {
        registration.update();
      }, UPDATE_CHECK_INTERVAL_MS);
      // Also check whenever the app regains focus (e.g. reopened from the
      // switcher), so a fresh deploy is noticed on the next launch.
      window.addEventListener('focus', () => registration.update());
    },
  });

  /**
   * Refresh, with a fallback because the library's own reload is not reliable
   * here.
   *
   * `updateServiceWorker(true)` tells the waiting worker to skip waiting and
   * reloads once `controllerchange` fires. In an installed iOS app that event
   * often never arrives, so the tap appeared to do nothing. If we are still on
   * the page shortly after, reload directly - with a cache-busting query, since
   * a plain reload can be answered by the old worker's precache.
   */
  const handleRefresh = async () => {
    try {
      await updateServiceWorker(true);
    } catch (error) {
      console.error('Service worker update failed:', error);
    }
    setTimeout(() => {
      const url = new URL(window.location.href);
      url.searchParams.set('updated', String(Date.now()));
      window.location.replace(url.toString());
    }, 1500);
  };

  if (!needRefresh) return null;

  return <UpdateToast onRefresh={handleRefresh} onDismiss={() => setNeedRefresh(false)} />;
}
